import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useIds } from "../../hook/AdminContext";
import {
  live_job_feed_detail,
  upload_file_on_s3,
  yourecruite_api,
} from "../../DAL/jobs/jobs";
import { enqueueSnackbar } from "notistack";
import { get_short_string, getFileIcon } from "../../utils/Constant";
import { s3baseUri } from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import { RemoveRedEyeOutlined, UploadFile } from "@mui/icons-material";
import { get_candidates_cv_list } from "../../DAL/cv-management/cv-management";
import { apply_job_api } from "../../DAL/TalentSourceAPI/TalentSourceApi";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Eye } from "lucide-react";
import DocumentModalboxCv from "../../pages/DocumentPreview/DocumentModalBoxcv";

export default function QuickApplyForm({
  job = null,
  handleNavigateBack,
  fetchDetails = () => {},
  setJobsList = () => {},
}) {
  const [uploading, setUploading] = useState(false);
  const [targetJob, setTargetJob] = useState(job);

  const [result_application, set_result_application] = useState({});

  const [existingCVList, setExistingCVList] = useState([]);
  const [selectedCV, setSelectedCV] = useState(null);

  const [cvDownloadLoading, setCvDownloadLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);

  const [cvName, setCvName] = useState(""); // State to store CV file name
  const [cvFile, setCVFile] = useState(); // State to store CV file name
  const [shortDescription, setShortDescription] = useState("");
  const [additionalDocDownloadLoading, setAdditionalDocDownloadLoading] =
    useState({});

  const [openCVDialog, setOpenCVDialog] = useState(false);
  const [targetCV, setTargetCV] = useState(null);

  const { userInfo } = useIds();

  const handleFileChangeAdditional = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "image/jpeg", // for .jpg and .jpeg
      "image/png", // for .png
    ];

    const validFiles = selectedFiles.filter((file) =>
      allowedTypes.includes(file.type)
    );

    if (validFiles.length !== selectedFiles.length) {
      enqueueSnackbar(
        "Only PDF, DOC, DOCX, XLS, XLSX, CSV, JPG, and PNG files are allowed",
        { variant: "error" }
      );
    }

    const maxFiles = 5;
    if (files.length + validFiles.length > maxFiles) {
      enqueueSnackbar("You can only upload up to 5 documents.", {
        variant: "error",
      });
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = null;
  };

  // Handle file selection
  const handleChangeCV = (event) => {
    const file = event.target.files[0];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar("Maximum allowed file size is 5MB.", {
        variant: "error",
      });
      return;
    }
    setCVFile(file);
    setSelectedCV(file);
    if (file) {
      setCvName(file.name);
    }
  };

  const handleCvNull = () => {
    setCVFile(null);
    setCvName(null);
    // Reset the file input element to allow selecting the same file again
    const fileInput = document.getElementById("cv-upload-quick-apply");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleDownloadAdditiondOC = async (documentPath) => {
    const fileUrl = `${s3baseUri}${documentPath}`;
    const filename = documentPath.split("/").pop();

    try {
      setAdditionalDocDownloadLoading((prev) => ({
        ...prev,
        [documentPath]: true,
      }));
      const response = await fetch(fileUrl, { mode: "cors" });
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
    } finally {
      setAdditionalDocDownloadLoading((prev) => ({
        ...prev,
        [documentPath]: false,
      }));
    }
  };
  const handleDownloadCV = async (cvPath) => {
    if (!cvPath) return;
    const fileUrl = `${s3baseUri}${cvPath}`;
    const filename = cvPath.split("/").pop();
    try {
      setCvDownloadLoading(true);
      const response = await fetch(fileUrl, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
    } finally {
      setCvDownloadLoading(false);
    }
  };

  const HandleApplyJob = async () => {
    if (!selectedCV) {
      enqueueSnackbar("Please attach your CV before applying!", {
        variant: "error",
      });
      return;
    }
    const newcv = selectedCV.name.toLowerCase();
    const allowedExtensions = [".pdf", ".doc", ".docx"];

    // Check if the file has a valid extension
    if (!allowedExtensions.some((ext) => newcv.endsWith(ext))) {
      enqueueSnackbar("Only PDF, DOC, and DOCX files are accepted!", {
        variant: "error",
      });
      return;
    }

    setIsSubmitting(true);
    let uploadedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await upload_file_on_s3(formData);
        if (res.code === 200) {
          uploadedFiles.push(res.image_path);
        }
      } catch (err) {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: "error" });
      }
    }

    const formData = new FormData();
    formData.append("job_slug", targetJob.job_slug);
    if (selectedCV instanceof window.File) {
      formData.append("cv", selectedCV);
      formData.append("upload_file", true);
      formData.append("profile_true", true);
    } else {
      formData.append("upload_file", false);
      formData.append("cv_path", selectedCV.path);
      formData.append("cv_name", selectedCV.name);
      formData.append("profile_true", false);
    }
    formData.append("job_description", shortDescription);
    formData.append("additional_documents", JSON.stringify(uploadedFiles));

    // if (targetJob.apply_notify_url) {
    //   handleCallYouRecruiteAPI();
    // }

    const response = await apply_job_api(formData);
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setLoading(false);
      setIsSubmitting(false);
      updateJobStatusToApplied();
      await fetchDetails();
      handleNavigateBack();
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCallYouRecruiteAPI = async () => {
    let cvFile = selectedCV;

    try {
      // If cv is NOT File: fetch file and convert
      if (!(cvFile instanceof window.File)) {
        if (!selectedCV?.path) {
          console.error("No path found for selectedCV");
          return;
        }

        // 1. Fetch file
        const response = await fetch(selectedCV.path);
        if (!response.ok) throw new Error("Failed to fetch CV file");

        // 2. Convert response → Blob
        const blob = await response.blob();

        // 3. Create a real File object with proper name + type
        const fileName =
          selectedCV.name || selectedCV.path.split("/").pop() || "cv.pdf";
        cvFile = new File([blob], fileName, { type: blob.type });
      }

      const formData = new FormData();
      formData.append("yourecruit_email", userInfo.email);
      formData.append("yourecruit_first_name", userInfo.first_name);
      formData.append("yourecruit_last_name", userInfo.last_name);
      formData.append("yourecruit_contact_number", userInfo.contact_number);
      formData.append("yourecruit_address", JSON.stringify(userInfo.address));

      // main CV file
      formData.append("yourecruit_cv_file", cvFile);
      formData.append("yourecruit_cover_letter", shortDescription);

      // additional files
      files.forEach((file, index) => {
        formData.append(`yourecruit_additional_files[${index}]`, file);
        formData.append(
          `yourecruit_additional_files_name[${index}]`,
          file.name
        );
      });

      // Call yourecruit API
      const resp = await yourecruite_api(targetJob.apply_notify_url, formData);
    } catch (error) {
      console.error("Error forwarding CV file:", error);
    }
  };

  const updateJobStatusToApplied = () => {
    setJobsList((prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((job) =>
        job._id === targetJob._id ? { ...job, applied_status: true } : job
      );
    });
  };

  const fetchJobDetails = async () => {
    setLoading(true);

    const response = await live_job_feed_detail(job.job_slug);
    if (response.code === 200) {
      if (response.job.applied_status) {
        enqueueSnackbar("Already Applied For This Job.", { variant: "info" });
        updateJobStatusToApplied();
        await fetchDetails();
        handleNavigateBack();
        return;
      }
      setTargetJob(response.job);
      set_result_application(response?.job?.job_application);
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
      setLoading(false);
    }
    setLoading(false);
  };

  const handleGetCVList = async () => {
    setLoading(true);
    const resp = await get_candidates_cv_list();
    if (resp.code == 200) {
      setExistingCVList(resp.data.cv_list);
      if (resp.data.cv_list.length > 0) {
        setSelectedCV(resp.data.cv_list[0]);
      }
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setLoading(false);
  };

  const EyeIcon = React.memo(({ onClick }) => (
    <Eye
      className="tw-cursor-pointer tw-text-primary me-2 tw-h-5 tw-w-5"
      onClick={onClick}
    />
  ));

  const CVItem = ({ cv, EyeIcon }) => {
    const isSelected =
      selectedCV instanceof window.File
        ? selectedCV == cv
        : selectedCV._id == cv._id;
    const cv_name = cv.name;

    return (
      <label
        htmlFor={`cv-${cv._id}`}
        key={`cv-${cv._id}`}
        className="w-100 mb-3 position-relative tw-flex tw-items-center tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-cursor-pointer tw-p-2 tw-transition tw-duration-150"
      >
        <input
          type="radio"
          id={`cv-${cv._id}`}
          name="primary_cv"
          value={cv}
          checked={isSelected}
          onChange={() => setSelectedCV(cv)}
          className="tw-h-4 tw-w-4 tw-text-primary tw-border-gray-300 tw-ring-primary focus:tw-ring-primary"
        />
        <div className="tw-ml-3 d-flex justify-content-between align-items-center w-100">
          <p className="mb-0 word-break">{get_short_string(cv_name, 30)}</p>
          <div className="d-flex align-items-center">
            {!(cv instanceof window.File) && (
              <EyeIcon
                key={`cv-${cv_name}`}
                onClick={() => {
                  setTargetCV(cv);
                  setOpenCVDialog(true);
                }}
              />
            )}
            {getFileIcon(cv_name)}
          </div>
        </div>

        {cv instanceof window.File && (
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: "-10px",
              right: "-5px",
              backgroundColor: "red",
              color: "white",
              padding: "1px",
              "&:hover": {
                backgroundColor: "red",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCvNull();
            }}
          >
            <CloseIcon sx={{ fontSize: "15px" }} />
          </IconButton>
        )}
      </label>
    );
  };

  const AdditionalDocItem = ({ file, index }) => {
    return (
      <div
        id={`doc-${file.name}`}
        className="w-100 mb-3 position-relative tw-flex tw-items-center tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-cursor-pointer tw-p-2 tw-transition tw-duration-150"
      >
        <div className="tw-ml-3 d-flex justify-content-between align-items-center w-100">
          <p className="mb-0">{get_short_string(file.name, 30)}</p>
          {getFileIcon(file.name)}
        </div>

        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: "-10px",
            right: "-5px",
            backgroundColor: "red",
            color: "white",
            padding: "1px",
            "&:hover": {
              backgroundColor: "red",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            removeFile(index);
          }}
        >
          <CloseIcon sx={{ fontSize: "15px" }} />
        </IconButton>
      </div>
    );
  };

  useEffect(() => {
    fetchJobDetails();
    handleGetCVList();
  }, []);
  return (
    <>
      {openCVDialog && (
        <DocumentModalboxCv
          open={openCVDialog}
          handleClose={() => {
            setOpenCVDialog(false);
          }}
          docName={targetCV.name}
          image_url={
            targetCV instanceof window.File
              ? URL.createObjectURL(targetCV)
              : s3baseUri + targetCV.path
          }
          previewDoc={userInfo}
        />
      )}
      {/* Upload Section for Other Users */}
      {targetJob.created_by !== userInfo?._id &&
        userInfo?.role == "candidate" &&
        !userInfo?.isCompanyProfile && (
          <>
            {/* CV Section with Tooltip */}

            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mt: 0,
                  gap: 2,
                }}
              >
                <h6>Upload Or Select CV *</h6>

                {!(
                  existingCVList.length == 5 ||
                  (existingCVList.length == 4 && cvFile)
                ) && (
                  <div className="d-flex flex-column align-items-end">
                    <label
                      htmlFor="cv-upload-quick-apply"
                      style={{
                        cursor:
                          existingCVList.length === 5 ||
                          (existingCVList.length === 4 && cvFile)
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          existingCVList.length === 5 ||
                          (existingCVList.length === 4 && cvFile)
                            ? 0.6
                            : 1,
                        pointerEvents:
                          existingCVList.length === 5 ||
                          (existingCVList.length === 4 && cvFile)
                            ? "none"
                            : "auto",
                      }}
                    >
                      <input
                        accept=".pdf,.doc,.docx"
                        id="cv-upload-quick-apply"
                        type="file"
                        hidden
                        onChange={handleChangeCV}
                        disabled={
                          existingCVList.length === 5 ||
                          (existingCVList.length === 4 && cvFile)
                        }
                      />
                      <button
                        type="button"
                        className="outlined-btn"
                        style={{ pointerEvents: "none" }} // allows label to handle click
                        disabled={
                          existingCVList.length === 5 ||
                          (existingCVList.length === 4 && cvFile)
                        }
                      >
                        <UploadFile fontSize="small" className="me-2" />
                        Upload CV
                      </button>
                    </label>
                    <span className="my-1">
                      Accepted files: pdf, docx, doc | Max Size: 5 MB
                    </span>
                  </div>
                )}
              </Box>

              <div className="row mt-3">
                {existingCVList.length > 0 &&
                  existingCVList.map((obj) => (
                    <div className="col-12 col-md-6">
                      <CVItem cv={obj} key={obj.name} EyeIcon={EyeIcon} />
                    </div>
                  ))}
                {cvFile && (
                  <div className="col-12 col-md-6">
                    <CVItem cv={cvFile} key={cvFile.name} EyeIcon={EyeIcon} />
                  </div>
                )}
              </div>
            </Box>
          </>
        )}
      {userInfo?.role == "candidate" && !userInfo?.isCompanyProfile && (
        <Divider className="my-2" />
      )}

      {targetJob.created_by !== userInfo?._id &&
        userInfo?.role == "candidate" &&
        !userInfo?.isCompanyProfile && (
          <>
            {/* Additional Documents Section with Tooltip */}

            <Box>
              <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                <div className="row w-100 div-style ms-0 pt-0">
                  <div className="col-lg-6 col-md-6 col-sm-12 p-0">
                    <h6>Additional Documents</h6>
                    <span className="text-sm" style={{ fontWeight: "normal" }}>
                      (You can upload up to 5 documents)
                    </span>
                  </div>

                  {!targetJob?.applied_status && (
                    <div className="col-lg-6 col-md-6 col-12 d-flex flex-column justify-content-end align-items-end">
                      <label
                        htmlFor="upload-additional-file-quick-apply"
                        className="cursor-pointer"
                      >
                        <input
                          type="file"
                          id="upload-additional-file-quick-apply"
                          name="documents"
                          multiple
                          accept=".pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png"
                          onChange={handleFileChangeAdditional}
                          style={{ display: "none" }}
                        />

                        <button
                          type="button"
                          className="outlined-btn"
                          style={{ pointerEvents: "none" }} // ensures the label click works
                        >
                          <UploadFile fontSize="small" className="me-2" />
                          {uploading ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "white", mr: 1 }}
                            />
                          ) : (
                            "Upload"
                          )}
                        </button>
                      </label>

                      <span className="my-1">
                        Allowed formats: pdf, doc, docx, xls, xlsx, jpg, png
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {files.length > 0 && (
                <>
                  <div className="row mt-3">
                    {files.length > 0 &&
                      files.map((file, index) => (
                        <div className="col-12 col-md-6">
                          <AdditionalDocItem file={file} index={index} />
                        </div>
                      ))}
                  </div>
                </>
              )}
            </Box>
          </>
        )}
      {userInfo?.role == "candidate" && !userInfo?.isCompanyProfile && (
        <Divider className="my-2" />
      )}
      <Box>
        {userInfo?.role == "candidate" && !userInfo?.isCompanyProfile && (
          <>
            <h6 className="mt-3">Cover Letter</h6>
            <Tooltip
              title={
                targetJob?.applied_status && result_application?.job_description
                  ? "This is your uploaded Cover Letter / Additional Information. You cannot edit it."
                  : ""
              }
              arrow
            >
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder={
                    targetJob?.applied_status
                      ? ""
                      : "Cover Letter / Additional Information"
                  }
                  value={
                    targetJob?.applied_status
                      ? result_application?.job_description || shortDescription
                      : shortDescription
                  }
                  disabled={targetJob?.applied_status}
                  onChange={(e) =>
                    !targetJob?.applied_status &&
                    setShortDescription(e.target.value)
                  }
                />
              </Box>
            </Tooltip>
          </>
        )}
      </Box>
      {/* Apply Button */}
      <Box display="flex" justifyContent="end" mt={2}>
        {targetJob.created_by !== userInfo?._id &&
          !targetJob?.applied_status &&
          userInfo?.role == "candidate" &&
          !userInfo?.isCompanyProfile && (
            <button
              onClick={HandleApplyJob}
              className="btn-contained-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                  Applying...
                </>
              ) : (
                "Apply now"
              )}
            </button>
          )}
      </Box>
    </>
  );
}
